/* eslint-disable no-new-func */
import type { ListItem } from 'global/Securities/types/store/portfolio';
import { securitiesStorageKey } from '../constants/localStorage';
import { ConfigFormula } from '../types/formula';

export const getConfigFormula = (): ConfigFormula =>
  JSON.parse(localStorage.getItem(securitiesStorageKey.stockbitFormula))
    ?.formula || {};

export const getSellAmountInvestedFee = (
  lot: number,
  price: number,
  isCorporateAction?: boolean,
  isDayTrade = false,
): number => {
  const {
    sell_amount_invested_fee: sellAmountInvestedFee,
    ca_sell_amount_invested_fee: caSellAmountInvestedFee,
    fee_sell: feeSell,
    fee_buy: feeBuy,
    fee_buy_day_trade: feeBuyDayTrade,
    fee_sell_day_trade: feeSellDayTrade,
  } = getConfigFormula();
  const FEE_BUY = isDayTrade ? feeBuyDayTrade : feeBuy;
  const FEE_SELL = isDayTrade ? feeSellDayTrade : feeSell;

  return new Function(
    `LOT=${lot},PRICE=${price},${
      isCorporateAction ? `FEE_BUY=${FEE_BUY}` : `FEE_SELL=${FEE_SELL}`
    }`,
    `return ${
      isCorporateAction ? caSellAmountInvestedFee : sellAmountInvestedFee
    }`,
  )();
};

export const getBuyAmountInvested = (lot: number, price: number): number => {
  const { buy_amount_invested: buyAmountInvested } = getConfigFormula();

  return new Function(
    `LOT=${lot},PRICE=${price}`,
    `return ${buyAmountInvested}`,
  )();
};

export const getBuyAmountInvestedFee = (
  lot: number,
  price: number,
  isDayTrade = false,
): number => {
  const {
    buy_amount_invested_fee: buyAmountInvestedFee,
    fee_buy: feeBuy,
    fee_buy_day_trade: feeBuyDayTrade,
  } = getConfigFormula();

  const FEE_BUY = isDayTrade ? feeBuyDayTrade : feeBuy;

  return new Function(
    `LOT=${lot},PRICE=${price},FEE_BUY=${FEE_BUY}`,
    `return ${buyAmountInvestedFee}`,
  )();
};

export const getProfitLoss = (
  sellAmountInvestedFee: number,
  buyAmountInvestedAveragePriceFee: number,
): number => {
  const { sell_profit_loss: sellProfitLoss } = getConfigFormula();

  return new Function(
    `SELL_AMOUNT_INVESTED_FEE=${sellAmountInvestedFee},BUY_AMOUNT_INVESTED_AVERAGE_PRICE_FEE=${buyAmountInvestedAveragePriceFee}`,
    `return ${sellProfitLoss}`,
  )();
};

export const getSellGain = (
  sellProfitLoss: number,
  buyAmountInvestedAveragePriceFee: number,
): number => {
  const { sell_gain: sellGain } = getConfigFormula();

  return (
    new Function(
      `SELL_PROFITLOSS=${sellProfitLoss},BUY_AMOUNT_INVESTED_AVERAGE_PRICE_FEE=${buyAmountInvestedAveragePriceFee}`,
      `return ${sellGain}`,
    )() * 100
  );
};

export const getLimitLot = (balance: number, price: number, isDayTrade = false) => {
  const priceWithFee = getBuyAmountInvestedFee(1, price, isDayTrade);
  const limitLot = Math.floor(balance / priceWithFee);
  return limitLot < 0 ? 0 : limitLot;
};

interface UpdatedDataPortfolio {
  unrealised_marketvalue: number;
  unrealised_profitloss: number;
  unrealised_gain: number;
}

export const generateUpdatedPortfolioItem = (
  configFormula: { formula: ConfigFormula },
  lastPrice: number,
  balanceLot: number,
  amountInvested: number,
): UpdatedDataPortfolio => {
  const { formula } = configFormula ?? { formula: getConfigFormula() };

  const LOT = balanceLot;
  const CURRENT_PRICE = lastPrice;
  const AMOUNT_INVESTED = amountInvested;

  const unrealised_market_value = new Function(
    'LOT',
    'CURRENT_PRICE',
    `return ${formula.unrealised_market_value};`,
  )(LOT, CURRENT_PRICE);

  const UNREALISED_MARKET_VALUE = unrealised_market_value;

  const unrealised_profit_loss = new Function(
    'UNREALISED_MARKET_VALUE',
    'AMOUNT_INVESTED',
    `return ${formula.unrealised_profit_loss};`,
  )(UNREALISED_MARKET_VALUE, AMOUNT_INVESTED);

  const UNREALISED_PROFIT_LOSS = unrealised_profit_loss;

  const unrealised_gain = new Function(
    'UNREALISED_PROFIT_LOSS',
    'AMOUNT_INVESTED',
    `return ${formula.unrealised_gain};`,
  )(UNREALISED_PROFIT_LOSS, AMOUNT_INVESTED);

  return {
    unrealised_marketvalue: unrealised_market_value,
    unrealised_profitloss: unrealised_profit_loss,
    unrealised_gain,
  };
};

interface UpdatedDataPortfolioHeader {
  unrealised_profitloss: number;
  gain: number;
  equity: number;
  ratio: number;
  market_value: number;
}

export const generateUpdatedPortfolioHeader = (
  configFormula: { formula: ConfigFormula },
  result: ListItem[] = [],
  dayTrade: ListItem[] = [],
  tradingBalance: number,
  amountInvested: number,
  amountAllocated: number,
  totalDebt: number,
): UpdatedDataPortfolioHeader => {
  const { formula } = configFormula ?? { formula: getConfigFormula() };

  const combinedResult = [...result, ...dayTrade];
  const unrealised_profitloss: number = combinedResult.reduce(
    (sum, item) =>
      item.unrealised_profitloss !== undefined
        ? sum + item.unrealised_profitloss
        : sum,
    0,
  );

  const SUMMARY_NET_PROFIT_LOSS = unrealised_profitloss;
  const TRADING_BALANCE = tradingBalance;
  const SUMMARY_OPEN = amountAllocated;
  const SUMMARY_INVESTED = amountInvested;

  const gain = new Function(
    'SUMMARY_NET_PROFIT_LOSS',
    'TRADING_BALANCE',
    'SUMMARY_OPEN',
    'SUMMARY_INVESTED',
    `return ${formula.summary_net_profit_loss_percentage};`,
  )(SUMMARY_NET_PROFIT_LOSS, TRADING_BALANCE, SUMMARY_OPEN, SUMMARY_INVESTED);

  const equity = new Function(
    'SUMMARY_NET_PROFIT_LOSS',
    'TRADING_BALANCE',
    'SUMMARY_OPEN',
    'SUMMARY_INVESTED',
    `return ${formula.summary_equity};`,
  )(SUMMARY_NET_PROFIT_LOSS, TRADING_BALANCE, SUMMARY_OPEN, SUMMARY_INVESTED);

  const unrealised_market_value_day_trade = dayTrade.reduce(
    (sum, item) =>
      item.unrealised_marketvalue !== undefined
        ? sum + item.unrealised_marketvalue
        : sum,
    0,
  );

  const TOTAL_DEBT = totalDebt;
  const DAY_TRADE_MARKET_VALUE = unrealised_market_value_day_trade;

  const ratio = new Function(
    'TOTAL_DEBT',
    'DAY_TRADE_MARKET_VALUE',
    `return ${formula.day_trade_debt_ratio};`,
  )(TOTAL_DEBT, DAY_TRADE_MARKET_VALUE);

  return {
    unrealised_profitloss,
    gain,
    equity,
    ratio: ratio * 100,
    market_value: DAY_TRADE_MARKET_VALUE,
  };
};
